import { Button, Input, Modal, Spin, Switch } from "antd";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { getDataRequest, postDataRequest } from "../../api/serviceMethods";
import { MEMBER_SETTING_URL } from "../../api/URLs";
import MessageConfirmModal from "../messageModal/MessageConfirmModal";
import { MemberSettingSvg } from "../../assets/images/HeaderSvg";
import EditTableSvg from "../../assets/images/EditTable";

const MemberSettingModal = (props) => {
    const {
        setModalOpen, modalOpen
    } = props;

    const { t } = useTranslation();
    const messageModalRef = useRef('');
    const [loading, setLoading] = useState(true);
    const [annualFee, setAnnualFee] = useState("");
    const [freePeriod, setFreePeriod] = useState("");
    const [shipFee, setShipFee] = useState("");
    const [data, setData] = useState(null);
    const [isFreeActive, setIsFreeActive] = useState(true);
    const [isSuperActive, setIsSuperActive] = useState(true);

    // Edit states for individual entries in configured view
    const [isEditingFree, setIsEditingFree] = useState(false);
    const [isEditingPro, setIsEditingPro] = useState(false);
    const [isEditingSuper, setIsEditingSuper] = useState(false);
    const [isDirty, setIsDirty] = useState(false);

    const initData = (itemData) => {
        setData(itemData || null);
        if (itemData) {
            setFreePeriod(itemData?.freePlan?.count !== undefined ? String(itemData.freePlan.count) : "");
            setShipFee(itemData?.proPlan?.amount !== undefined ? String(itemData.proPlan.amount) : "");
            setAnnualFee(itemData?.superPlan?.amount !== undefined ? String(itemData.superPlan.amount) : "");
            setIsFreeActive(itemData?.freePlan?.isActive !== undefined ? Boolean(itemData?.freePlan?.isActive) : Boolean(itemData?.freePlan?.count));
            setIsSuperActive(itemData?.superPlan?.isActive !== undefined ? Boolean(itemData?.superPlan?.isActive) : Boolean(itemData?.superPlan?.amount));
        } else {
            setFreePeriod("");
            setShipFee("");
            setAnnualFee("");
            setIsFreeActive(true);
            setIsSuperActive(true);
        }
        setIsEditingFree(false);
        setIsEditingPro(false);
        setIsEditingSuper(false);
        setIsDirty(false);
    };

    const getDataRequestAll = async () => {
        try {
            setLoading(true);
            const res = await getDataRequest(`${MEMBER_SETTING_URL}`);
            if (res.status === 200 && res.data) {
                initData(res.data);
            } else {
                initData(null);
            }
        } catch (err) {
            console.error("Error fetching member settings:", err);
            initData(null);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (modalOpen) {
            getDataRequestAll();
        }
    }, [modalOpen]);

    const resetInput = () => {
        setFreePeriod("");
        setShipFee("");
        setAnnualFee("");
        setIsFreeActive(true);
        setIsSuperActive(true);
        setIsEditingFree(false);
        setIsEditingPro(false);
        setIsEditingSuper(false);
        setIsDirty(false);
        setData(null);
    };

    const handleToggleFreeActive = (checked) => {
        if (!checked) {
            messageModalRef.current.showWarningConfirm(
                t("Are you sure you want to update the free trial status ?"),
                () => {
                    setIsFreeActive(false);
                    setIsEditingFree(false);
                    setIsDirty(true);
                },
                "",
                true
            );
        } else {
            setIsFreeActive(true);
            setIsDirty(true);
        }
    };

    const handleToggleSuperActive = (checked) => {
        if (!checked) {
            messageModalRef.current.showWarningConfirm(
                t("Are you sure you want to update the super membership status ?"),
                () => {
                    setIsSuperActive(false);
                    setIsEditingSuper(false);
                    setIsDirty(true);
                },
                "",
                true
            );
        } else {
            setIsSuperActive(true);
            setIsDirty(true);
        }
    };

    const handleSubmit = () => {
        messageModalRef.current.showWarningConfirm(
            t("Are you sure you want to update the membership settings?"),
            async () => {
                const params = {
                    freePlan: {
                        amount: 0,
                        currency: "CNY",
                        type: "free",
                        unit: "day",
                        count: freePeriod ? Number(freePeriod) : Number(data?.freePlan?.count || 0),
                        isActive: isFreeActive
                    },
                    proPlan: {
                        amount: shipFee !== "" && shipFee !== null ? Number(shipFee) : Number(data?.proPlan?.amount || 0),
                        currency: "CNY",
                        type: "pro",
                        unit: "year",
                        count: 1,
                        isActive: true
                    },
                    superPlan: {
                        amount: annualFee !== "" && annualFee !== null ? Number(annualFee) : Number(data?.superPlan?.amount || 0),
                        currency: "CNY",
                        type: "super",
                        unit: "year",
                        count: 1,
                        isActive: isSuperActive
                    }
                };

                try {
                    setLoading(true);
                    const res = await postDataRequest(`${MEMBER_SETTING_URL}`, params);
                    if (res.status === 200) {
                        messageModalRef.current.showSuccessConfirmsAutoClose(t("Saved Successfully"),
                            () => { },
                            "",
                            true
                        );
                        setIsEditingFree(false);
                        setIsEditingPro(false);
                        setIsEditingSuper(false);
                        setIsDirty(false);
                        setModalOpen(false);
                        resetInput();
                    } else {
                        messageModalRef.current.showWarningConfirmsAutoClose(
                            (res && t(res.message)) || t("An error occurred"),
                            () => { },
                            "",
                            true
                        );
                    }
                } catch (err) {
                    messageModalRef.current.showWarningConfirmsAutoClose(
                        (err && err.response && err.response.data && err.response.data.message) || t("Server Error!"),
                        () => { },
                        "",
                        true
                    );
                } finally {
                    setLoading(false);
                }
            },
            "",
            true
        );
    };

    const isModified = isEditingFree || isEditingPro || isEditingSuper || isDirty;
    const isSubmitDisabled = !data
        ? (!shipFee || (isFreeActive && !freePeriod) || (isSuperActive && !annualFee))
        : ((isEditingFree && isFreeActive && !freePeriod) || (isEditingPro && !shipFee) || (isEditingSuper && isSuperActive && !annualFee));

    const cardStyle = {
        background: "#ffffff",
        border: "1px solid #EDEDED",
        borderRadius: "8px",
        padding: "16px 20px",
        marginBottom: "14px",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        minHeight: "56px"
    };

    const labelStyle = {
        fontSize: "14px",
        color: "#636E88",
        fontWeight: 400,
        marginRight: "14px"
    };

    const valueStyle = {
        fontSize: "14px",
        color: "#2C3E50",
        fontWeight: 600
    };

    const editIconStyle = {
        cursor: "pointer",
        display: "inline-flex",
        alignItems: "center",
        marginLeft: "12px"
    };

    return (
        <>
            <Modal
                title={
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                        <MemberSettingSvg />
                        <span style={{ marginLeft: "10px", fontSize: "16px", fontWeight: 600, color: "#2C3E50" }}>
                            {t('Member Setting')}
                        </span>
                    </div>
                }
                centered
                width={540}
                open={modalOpen}
                className='custom-modal'
                onOk={() => {
                    setModalOpen(false);
                    resetInput();
                }}
                onCancel={() => {
                    setModalOpen(false);
                    resetInput();
                }}
                cancelButtonProps={{ style: { display: 'none' } }}
                okButtonProps={{ style: { display: 'none' } }}
            >
                <Spin spinning={loading}>
                    <hr style={{ margin: "14px 0 20px 0", borderTop: "1px solid #f0f0f0" }} />
                    <div style={{ padding: "4px 20px 10px 20px" }}>
                        {data ? (
                            /* Configured View / Partial Editing View */
                            <>
                                {/* Row 1: Free trial period */}
                                <div style={cardStyle}>
                                    <div style={{ display: "flex", alignItems: "center", flex: 1 }}>
                                        <span style={labelStyle}>{t("Free trial period")}:</span>
                                        {isEditingFree ? (
                                            <div style={{ display: "flex", alignItems: "center" }}>
                                                <Input
                                                    size="middle"
                                                    type="number"
                                                    min={1}
                                                    style={{ width: "88px", marginRight: "6px" }}
                                                    value={freePeriod}
                                                    onChange={(e) => {
                                                        setFreePeriod(e.target.value);
                                                        setIsDirty(true);
                                                    }}
                                                />
                                                <span style={{ color: "#636E88", fontSize: "13px" }}>{t("day")}</span>
                                            </div>
                                        ) : (
                                            <span style={valueStyle}>
                                                {data?.freePlan?.count
                                                    ? `${data?.freePlan?.count}${data?.freePlan?.unit === "month" ? t("months") : " " + t("day")}`
                                                    : `${freePeriod || 0} ${t("day")}`}
                                            </span>
                                        )}
                                    </div>
                                    <div style={{ display: "flex", alignItems: "center" }}>
                                        <Switch
                                            checked={isFreeActive}
                                            onChange={handleToggleFreeActive}
                                        />
                                        {!isEditingFree && isFreeActive && (
                                            <span
                                                style={editIconStyle}
                                                onClick={() => setIsEditingFree(true)}
                                                title={t("Edit")}
                                            >
                                                <EditTableSvg />
                                            </span>
                                        )}
                                    </div>
                                </div>

                                {/* Row 2: Annual membership fee */}
                                <div style={cardStyle}>
                                    <div style={{ display: "flex", alignItems: "center", flex: 1 }}>
                                        <span style={labelStyle}>{t("Annual membership fee")} :</span>
                                        {isEditingPro ? (
                                            <div style={{ display: "flex", alignItems: "center" }}>
                                                <Input
                                                    size="middle"
                                                    type="number"
                                                    min={0}
                                                    style={{ width: "88px", marginRight: "6px" }}
                                                    value={shipFee}
                                                    onChange={(e) => {
                                                        setShipFee(e.target.value);
                                                        setIsDirty(true);
                                                    }}
                                                />
                                                <span style={{ color: "#636E88", fontSize: "13px" }}>{t("yuan/year")}</span>
                                            </div>
                                        ) : (
                                            <span style={valueStyle}>
                                                {data?.proPlan?.amount !== undefined ? `${data?.proPlan?.amount} ${t("yuan/year")}` : `${shipFee || 0} ${t("yuan/year")}`}
                                            </span>
                                        )}
                                    </div>
                                    <div style={{ display: "flex", alignItems: "center" }}>
                                        {!isEditingPro && (
                                            <span
                                                style={editIconStyle}
                                                onClick={() => setIsEditingPro(true)}
                                                title={t("Edit")}
                                            >
                                                <EditTableSvg />
                                            </span>
                                        )}
                                    </div>
                                </div>

                                {/* Row 3: Super membership annual fee */}
                                <div style={cardStyle}>
                                    <div style={{ display: "flex", alignItems: "center", flex: 1 }}>
                                        <span style={labelStyle}>{t("Super membership annual fee")} :</span>
                                        {isEditingSuper ? (
                                            <div style={{ display: "flex", alignItems: "center" }}>
                                                <Input
                                                    size="middle"
                                                    type="number"
                                                    min={0}
                                                    style={{ width: "88px", marginRight: "6px" }}
                                                    value={annualFee}
                                                    onChange={(e) => {
                                                        setAnnualFee(e.target.value);
                                                        setIsDirty(true);
                                                    }}
                                                />
                                                <span style={{ color: "#636E88", fontSize: "13px" }}>{t("yuan/year")}</span>
                                            </div>
                                        ) : (
                                            <span style={valueStyle}>
                                                {data?.superPlan?.amount !== undefined
                                                    ? `${data?.superPlan?.amount} ${t("yuan/year")}`
                                                    : `${annualFee || 0} ${t("yuan/year")}`}
                                            </span>
                                        )}
                                    </div>
                                    <div style={{ display: "flex", alignItems: "center" }}>
                                        <Switch
                                            checked={isSuperActive}
                                            onChange={handleToggleSuperActive}
                                        />
                                        {!isEditingSuper && isSuperActive && (
                                            <span
                                                style={editIconStyle}
                                                onClick={() => setIsEditingSuper(true)}
                                                title={t("Edit")}
                                            >
                                                <EditTableSvg />
                                            </span>
                                        )}
                                    </div>
                                </div>
                            </>
                        ) : (
                            /* Initial Setup View (when no data exists) */
                            <>
                                {/* Row 1: Free trial period */}
                                <div style={cardStyle}>
                                    <div style={{ display: "flex", alignItems: "center", flex: 1 }}>
                                        <span style={labelStyle}>{t("Free trial period")}:</span>
                                        <Input
                                            size="middle"
                                            type="number"
                                            min={1}
                                            style={{
                                                width: "88px",
                                                marginRight: "6px",
                                                backgroundColor: !isFreeActive ? "#f5f5f5" : "#ffffff",
                                                cursor: !isFreeActive ? "not-allowed" : "text"
                                            }}
                                            value={freePeriod}
                                            disabled={!isFreeActive}
                                            onChange={(e) => setFreePeriod(e.target.value)}
                                        />
                                        <span style={{ color: "#636E88", fontSize: "13px" }}>{t("day")}</span>
                                    </div>
                                    <div>
                                        <Switch
                                            checked={isFreeActive}
                                            onChange={(checked) => {
                                                if (!checked) {
                                                    messageModalRef.current.showWarningConfirm(
                                                        t("Are you sure you want to update the free trial status ?"),
                                                        () => {
                                                            setIsFreeActive(false);
                                                        },
                                                        "",
                                                        true
                                                    );
                                                } else {
                                                    setIsFreeActive(true);
                                                }
                                            }}
                                        />
                                    </div>
                                </div>

                                {/* Row 2: Annual membership fee */}
                                <div style={cardStyle}>
                                    <div style={{ display: "flex", alignItems: "center", flex: 1 }}>
                                        <span style={labelStyle}>{t("Annual membership fee")} :</span>
                                        <Input
                                            size="middle"
                                            type="number"
                                            min={0}
                                            style={{ width: "88px", marginRight: "6px" }}
                                            value={shipFee}
                                            onChange={(e) => setShipFee(e.target.value)}
                                        />
                                        <span style={{ color: "#636E88", fontSize: "13px" }}>{t("yuan/year")}</span>
                                    </div>
                                </div>

                                {/* Row 3: Super membership annual fee */}
                                <div style={cardStyle}>
                                    <div style={{ display: "flex", alignItems: "center", flex: 1 }}>
                                        <span style={labelStyle}>{t("Super membership annual fee")} :</span>
                                        <Input
                                            size="middle"
                                            type="number"
                                            min={0}
                                            style={{
                                                width: "88px",
                                                marginRight: "6px",
                                                backgroundColor: !isSuperActive ? "#f5f5f5" : "#ffffff",
                                                cursor: !isSuperActive ? "not-allowed" : "text"
                                            }}
                                            value={annualFee}
                                            disabled={!isSuperActive}
                                            onChange={(e) => setAnnualFee(e.target.value)}
                                        />
                                        <span style={{ color: "#636E88", fontSize: "13px" }}>{t("yuan/year")}</span>
                                    </div>
                                    <div>
                                        <Switch
                                            checked={isSuperActive}
                                            onChange={(checked) => {
                                                if (!checked) {
                                                    messageModalRef.current.showWarningConfirm(
                                                        t("Are you sure you want to update the super membership status ?"),
                                                        () => {
                                                            setIsSuperActive(false);
                                                        },
                                                        "",
                                                        true
                                                    );
                                                } else {
                                                    setIsSuperActive(true);
                                                }
                                            }}
                                        />
                                    </div>
                                </div>
                            </>
                        )}

                        {/* Submit button: shown in initial setup OR when any field/switch is modified in configured view */}
                        {(!data || isModified) && (
                            <div className='d-flex justify-content-center' style={{ marginTop: "24px", marginBottom: "6px" }}>
                                <Button
                                    disabled={isSubmitDisabled}
                                    style={{
                                        backgroundColor: "#E50A47",
                                        borderColor: "#E50A47",
                                        color: "#ffffff",
                                        height: "40px",
                                        width: "180px",
                                        borderRadius: "8px",
                                        fontSize: "15px",
                                        fontWeight: 500,
                                        opacity: isSubmitDisabled ? 0.5 : 1,
                                        cursor: isSubmitDisabled ? "not-allowed" : "pointer"
                                    }}
                                    onClick={handleSubmit}
                                >
                                    {data ? t("Confirm Edit") : t("Confirm Submit")}
                                </Button>
                            </div>
                        )}
                    </div>
                </Spin>
            </Modal>
            <MessageConfirmModal textCentered ref={messageModalRef} />
        </>
    );
};

export default MemberSettingModal;